import React from 'react';
import { Box, Tooltip, Chip } from '@mui/material';
import { styled } from '@mui/material/styles';

// 🎨 Styled components for nested bubbles display
const BubblesContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  flexWrap: 'wrap',
  maxWidth: '200px'
}));

const KeywordBubble = styled(Box)(({ theme, color }) => {
  const colors = {
    green: {
      background: 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)',
      shadow: '0 2px 8px rgba(76, 175, 80, 0.3)'
    },
    yellow: {
      background: 'linear-gradient(135deg, #ff9800 0%, #ffb74d 100%)',
      shadow: '0 2px 8px rgba(255, 152, 0, 0.3)'
    },
    red: {
      background: 'linear-gradient(135deg, #f44336 0%, #ef5350 100%)',
      shadow: '0 2px 8px rgba(244, 67, 54, 0.3)'
    },
    gray: {
      background: 'linear-gradient(135deg, #9e9e9e 0%, #bdbdbd 100%)',
      shadow: '0 2px 8px rgba(158, 158, 158, 0.3)'
    }
  };

  return {
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    background: colors[color]?.background || colors.gray.background,
    boxShadow: colors[color]?.shadow || colors.gray.shadow,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    fontSize: '10px',
    fontWeight: 'bold',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'scale(1.2)',
      zIndex: 10,
    }
  };
});

const OverflowIndicator = styled(Chip)(({ theme }) => ({
  height: '20px',
  fontSize: '10px',
  fontWeight: 'bold',
  backgroundColor: theme.palette.grey[300],
  color: theme.palette.grey[700],
  '& .MuiChip-label': {
    padding: '0 6px'
  }
}));

const KeywordBubbles = ({ keywordIndicators = [], totalKeywords = 0 }) => {
  // Show max 5 bubbles
  const visibleKeywords = keywordIndicators.slice(0, 5);
  const hiddenCount = Math.max(0, totalKeywords - 5);

  if (!keywordIndicators.length) {
    return (
      <Box sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
        Keine Keywords
      </Box>
    );
  }

  return (
    <BubblesContainer>
      {visibleKeywords.map((keyword, index) => {
        // Handle both object format {keyword, rank, color} and string format 'green'
        const isObject = typeof keyword === 'object';
        const color = isObject ? keyword.color : keyword;
        const rank = isObject ? keyword.rank : null;
        const keywordName = isObject ? keyword.keyword : `Keyword ${index + 1}`;
        const lastUpdated = isObject ? keyword.lastUpdated : null;
        
        return (
          <Tooltip
            key={index}
            title={
              <Box>
                <Box sx={{ fontWeight: 'bold' }}>{keywordName}</Box>
                <Box>Ranking: {rank || 'Unbekannt'}</Box>
                {lastUpdated && (
                  <Box sx={{ fontSize: '0.75rem', opacity: 0.8 }}>
                    Letzte Aktualisierung: {new Date(lastUpdated).toLocaleDateString('de-DE')}
                  </Box>
                )}
              </Box>
            }
            arrow
            placement="top"
          >
            <KeywordBubble color={color}>
              {rank && rank <= 99 ? rank : '?'}
            </KeywordBubble>
          </Tooltip>
        );
      })}
      
      {hiddenCount > 0 && (
        <Tooltip title={`${hiddenCount} weitere Keywords`} arrow>
          <OverflowIndicator 
            label={`+${hiddenCount}`}
            size="small"
          />
        </Tooltip>
      )}
    </BubblesContainer>
  );
};

export default KeywordBubbles;